import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Gallery = ({ images, showFilter = true }) => {
  const reduce = useReducedMotion();
  const categories = useMemo(
    () => ["All", ...new Set(images.map((i) => i.category))],
    [images]
  );
  const [active, setActive] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = useMemo(
    () => (active === "All" ? images : images.filter((i) => i.category === active)),
    [active, images]
  );

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelected(null);
      if (e.key === "ArrowRight") setSelected((s) => (s + 1) % filtered.length);
      if (e.key === "ArrowLeft") setSelected((s) => (s - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, filtered.length]);

  return (
    <div>
      {showFilter && (
        <div className="mb-8 flex flex-wrap gap-2" role="group" aria-label="Filter gallery by category">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              aria-pressed={active === c}
              onClick={() => setActive(c)}
              className={`min-h-[44px] rounded-full border px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                active === c
                  ? "border-brand bg-brand text-white"
                  : "border-brand-line bg-white text-brand hover:bg-brand-light"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <motion.div layout className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {filtered.map((img, i) => (
            <motion.button
              layout
              key={img.src}
              type="button"
              data-testid={`gallery-item-${i}`}
              onClick={() => setSelected(i)}
              initial={reduce ? false : { opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.3 }}
              className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-brand-line bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              aria-label={`Open image: ${img.alt}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                width="800"
                height="600"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="absolute bottom-2 left-2 rounded-full bg-brand-dark/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                {img.category}
              </span>
            </motion.button>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {selected !== null && filtered[selected] && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={filtered[selected].alt}
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-ink/90 p-4 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <button
              type="button"
              data-testid="lightbox-close-button"
              aria-label="Close image viewer"
              onClick={() => setSelected(null)}
              className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              type="button"
              data-testid="lightbox-prev-button"
              aria-label="Previous image"
              onClick={(e) => {
                e.stopPropagation();
                setSelected((s) => (s - 1 + filtered.length) % filtered.length);
              }}
              className="absolute left-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-6"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <motion.figure
              key={filtered[selected].src}
              initial={reduce ? false : { opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-h-[85vh] max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={filtered[selected].src}
                alt={filtered[selected].alt}
                className="max-h-[78vh] w-auto rounded-2xl object-contain"
              />
              <figcaption className="mt-3 text-center text-sm text-slate-300">
                {filtered[selected].alt} — {filtered[selected].category}
              </figcaption>
            </motion.figure>
            <button
              type="button"
              data-testid="lightbox-next-button"
              aria-label="Next image"
              onClick={(e) => {
                e.stopPropagation();
                setSelected((s) => (s + 1) % filtered.length);
              }}
              className="absolute right-2 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-6"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
